import { QuoteExtraApiModel } from '~/lib/api/smartsend/types';

/**
 * True when the extra's price is a placeholder zero rather than a real amount.
 *
 * Some courier pass-through extras (UPS DAP's additional handling, carbon neutral, direct delivery and
 * the residential legs) are always offered but only priced once the shipment is re-rated with the option
 * on. Until then the quote carries a well-formed £0 and flags it with `pricePending` - the number itself
 * cannot tell you it is a placeholder.
 *
 * This matters because a zero price otherwise renders as "FREE", which would advertise an extra the
 * courier is about to charge for as free of charge.
 *
 * Read the flag only through here so there is one source of truth (mirrors ui-checkout's
 * `isExtraPricePending`). Three wire quirks are deliberately absorbed:
 *  - the key is camelCase, unlike its lowercase siblings `autoapply` / `defaultselected`
 *  - the value arrives **capitalised** as `"True"`. The edge flattens extra metadata through
 *    `JsonElement.ToString()` (`ToQuoteExtraMeta`), which renders a JSON boolean via `bool.ToString()`
 *    rather than the JSON spelling - the same trap the address rules hit. Compared case-insensitively so
 *    either spelling works, matching `BooleanRuleIsOn`.
 *  - a real boolean is accepted too, in case that flattening ever changes. Being lenient is deliberate:
 *    the failure mode of a strict compare is advertising a chargeable extra as "FREE", which is the worse
 *    direction to be wrong in.
 *
 * Absent metadata means not pending: the flag is only emitted on the extras that support it.
 */
export const IsExtraPricePending = (extra: Pick<QuoteExtraApiModel, 'Details'> | undefined): boolean => {
  const flag = extra?.Details?.pricePending as string | boolean | undefined;
  return flag === true || String(flag).toLowerCase() === 'true';
};
